import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormUploader } from '@/components/ui/form-fields/form-uploader';
import { Icon } from '@/components/ui/icon';
import { apiClient } from '@/lib/api-client';
import {
  isSizeBreakdownComplete,
  type SizeBreakdownEntry,
  sizeBreakdownTotal,
} from '@/lib/sale-item-sizes';
import { cn } from '@/lib/utils';
import { useDynamicList } from '@/services/dynamic-resource';
import { QuantityStepper, ToggleChip, WizardField, WizardSelect } from './wizard-fields';

export type { SizeBreakdownEntry };

export interface DraftItem {
  key: string;
  shirtTypeId: string;
  shirtTypeLabel: string;
  shirtTypePrice: number;
  sleeveTypeId: string;
  sleeveTypeLabel: string;
  sleeveTypePrice: number;
  collarTypeId: string;
  collarTypeLabel: string;
  collarTypePrice: number;
  fabricTypeId: string;
  fabricTypeLabel: string;
  fabricTypePrice: number;
  printTypeId: string;
  printTypeLabel: string;
  printTypePrice: number;
  /** Vazio = tamanho ainda não informado (comum no orçamento — cliente decide depois). */
  sizeBreakdown: SizeBreakdownEntry[];
  quantity: number;
  selectedOptions: string[];
  artworkPath?: string;
  unitaryPrice: number;
  totalPrice: number;
}

type CatalogItem = { id: string; description: string; price: number };

export interface AdditionalOption {
  id: string;
  label: string;
  slug: string;
  scope: 'item' | 'sale';
  valueType: 'percentage' | 'fixed';
  value: number;
  isActive: boolean;
}

/** Soma o adicional (% ou fixo) das opções selecionadas em cima de um valor base. */
export function applyOptionsSurcharge(
  base: number,
  options: AdditionalOption[],
  selectedIds: string[],
) {
  return options
    .filter((option) => selectedIds.includes(option.id))
    .reduce((sum, option) => {
      const amount =
        option.valueType === 'percentage' ? base * (option.value / 100) : option.value;
      return sum + amount;
    }, 0);
}

/** Lista as opções `related` compatíveis com `ownerId` via a rota aninhada N:N. */
function useCompatible(resource: string, ownerId: string | undefined, related: string) {
  return useQuery({
    queryKey: ['compatible', resource, ownerId, related],
    queryFn: async () => {
      const res = await (apiClient as any)[resource]({ id: ownerId })[related].get();
      return (res.data ?? []) as CatalogItem[];
    },
    enabled: !!ownerId,
  });
}

const emptyDraft = (): Omit<DraftItem, 'key'> => ({
  shirtTypeId: '',
  shirtTypeLabel: '',
  shirtTypePrice: 0,
  sleeveTypeId: '',
  sleeveTypeLabel: '',
  sleeveTypePrice: 0,
  collarTypeId: '',
  collarTypeLabel: '',
  collarTypePrice: 0,
  fabricTypeId: '',
  fabricTypeLabel: '',
  fabricTypePrice: 0,
  printTypeId: '',
  printTypeLabel: '',
  printTypePrice: 0,
  sizeBreakdown: [],
  quantity: 1,
  selectedOptions: [],
  artworkPath: undefined,
  unitaryPrice: 0,
  totalPrice: 0,
});

interface ItemFormProps {
  editing?: DraftItem | null;
  /** Prefill de "duplicar item": mesmos dados, mas sempre vira um item novo ao salvar. */
  duplicateFrom?: DraftItem | null;
  itemNumber: number;
  onSave: (item: DraftItem) => void;
  onCancelEdit?: () => void;
}

/**
 * Formulário de um item do pedido: escolhe camisa e, a partir dela, só mostra
 * manga/gola/tecido/estampa compatíveis (relação N:N). Cada componente aparece
 * na tela só depois que o anterior na cadeia é escolhido (camisa → manga/gola →
 * tecido → estampa), com uma pequena animação de entrada — deixa claro o que
 * ainda falta escolher, em vez de jogar tudo na tela de uma vez.
 * Cada preço é uma cópia do catálogo no momento da escolha — editável aqui sem
 * alterar o cadastro.
 */
export function ItemForm({
  editing,
  duplicateFrom,
  itemNumber,
  onSave,
  onCancelEdit,
}: ItemFormProps) {
  const prefillSource = editing ?? duplicateFrom;
  const [draft, setDraft] = useState<Omit<DraftItem, 'key'>>(
    prefillSource ?? emptyDraft(),
  );
  const [hasArtwork, setHasArtwork] = useState(!!prefillSource?.artworkPath);
  // Desmarcado por padrão — muita venda é orçada sem o cliente ter decidido os tamanhos
  // ainda, então informar tamanho agora é opcional (fica pendente até a edição do pedido).
  const [informSizes, setInformSizes] = useState(!!prefillSource?.sizeBreakdown?.length);
  const artworkForm = useForm<{ artworkPath?: string }>({
    defaultValues: { artworkPath: prefillSource?.artworkPath },
  });
  const artworkPathValue = artworkForm.watch('artworkPath');

  useEffect(() => {
    setDraft(prefillSource ?? emptyDraft());
    setHasArtwork(!!prefillSource?.artworkPath);
    setInformSizes(!!prefillSource?.sizeBreakdown?.length);
    artworkForm.reset({ artworkPath: prefillSource?.artworkPath });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, duplicateFrom]);

  useEffect(() => {
    setDraft((d) => ({ ...d, artworkPath: hasArtwork ? artworkPathValue : undefined }));
  }, [artworkPathValue, hasArtwork]);

  const { data: shirtTypes } = useDynamicList('shirt-types', { perPage: 100 });
  const { data: sleeveOptions } = useCompatible(
    'shirt-types',
    draft.shirtTypeId,
    'sleeve-types',
  );
  const { data: collarOptions } = useCompatible(
    'shirt-types',
    draft.shirtTypeId,
    'collar-types',
  );
  const { data: fabricOptions } = useCompatible(
    'sleeve-types',
    draft.sleeveTypeId,
    'fabric-types',
  );
  const { data: printOptions } = useCompatible(
    'fabric-types',
    draft.fabricTypeId,
    'print-types',
  );
  const { data: shirtSizesResponse } = useDynamicList('shirt-sizes', {
    isActive: 'true',
    perPage: 100,
  });
  const shirtSizes: { id: string; description: string }[] =
    shirtSizesResponse?.data ?? [];
  const { data: itemOptionsResponse } = useDynamicList('additional-options', {
    scope: 'item',
    isActive: 'true',
    perPage: 100,
  });
  const itemOptions: AdditionalOption[] = itemOptionsResponse?.data ?? [];

  const baseUnitaryPrice =
    draft.shirtTypePrice +
    draft.sleeveTypePrice +
    draft.collarTypePrice +
    draft.fabricTypePrice +
    draft.printTypePrice;
  const unitaryPrice =
    baseUnitaryPrice +
    applyOptionsSurcharge(baseUnitaryPrice, itemOptions, draft.selectedOptions);
  const totalPrice = unitaryPrice * (draft.quantity || 0);

  const toggleOption = (optionId: string, checked: boolean) => {
    setDraft((d) => ({
      ...d,
      selectedOptions: checked
        ? [...d.selectedOptions, optionId]
        : d.selectedOptions.filter((id) => id !== optionId),
    }));
  };

  const sizesAssigned = sizeBreakdownTotal(draft.sizeBreakdown);
  const sizesRemaining = draft.quantity - sizesAssigned;

  const missing: string[] = [];
  if (!draft.shirtTypeId) missing.push('Tipo de camisa');
  if (!draft.sleeveTypeId) missing.push('Manga');
  if (!draft.collarTypeId) missing.push('Gola');
  if (!draft.fabricTypeId) missing.push('Tecido');
  if (!draft.printTypeId) missing.push('Estampa');
  if (!(draft.quantity > 0)) missing.push('Quantidade');
  if (!(baseUnitaryPrice > 0)) missing.push('Preço da composição (está zerado)');
  // Se marcou "Informar tamanho", ou completa certinho ou não salva com número errado —
  // sem meio-termo. Se não marcou, tamanho fica pendente (resolvido depois, na edição).
  if (informSizes && !isSizeBreakdownComplete(draft.quantity, draft.sizeBreakdown)) {
    missing.push(
      sizesRemaining > 0
        ? `grade de tamanhos — informe mais ${sizesRemaining}`
        : `grade de tamanhos (${Math.abs(sizesRemaining)} a mais que a quantidade)`,
    );
  }

  const canSave = missing.length === 0;

  const handleSelectShirt = (id: string) => {
    const shirt = (shirtTypes?.data ?? []).find((s: any) => s.id === id);
    setDraft({
      ...emptyDraft(),
      shirtTypeId: id,
      shirtTypeLabel: shirt?.description ?? '',
      shirtTypePrice: shirt?.price ?? 0,
    });
  };

  const handleSelectSleeve = (id: string) => {
    const sleeve = (sleeveOptions ?? []).find((s) => s.id === id);
    setDraft((d) => ({
      ...d,
      sleeveTypeId: id,
      sleeveTypeLabel: sleeve?.description ?? '',
      sleeveTypePrice: sleeve?.price ?? 0,
      fabricTypeId: '',
      fabricTypeLabel: '',
      fabricTypePrice: 0,
      printTypeId: '',
      printTypeLabel: '',
      printTypePrice: 0,
    }));
  };

  const handleSelectCollar = (id: string) => {
    const collar = (collarOptions ?? []).find((c) => c.id === id);
    setDraft((d) => ({
      ...d,
      collarTypeId: id,
      collarTypeLabel: collar?.description ?? '',
      collarTypePrice: collar?.price ?? 0,
    }));
  };

  const handleSelectFabric = (id: string) => {
    const fabric = (fabricOptions ?? []).find((f) => f.id === id);
    setDraft((d) => ({
      ...d,
      fabricTypeId: id,
      fabricTypeLabel: fabric?.description ?? '',
      fabricTypePrice: fabric?.price ?? 0,
      printTypeId: '',
      printTypeLabel: '',
      printTypePrice: 0,
    }));
  };

  const handleSelectPrint = (id: string) => {
    const print = (printOptions ?? []).find((p) => p.id === id);
    setDraft((d) => ({
      ...d,
      printTypeId: id,
      printTypeLabel: print?.description ?? '',
      printTypePrice: print?.price ?? 0,
    }));
  };

  const handleToggleInformSizes = (checked: boolean) => {
    setInformSizes(checked);
    if (!checked) setDraft((d) => ({ ...d, sizeBreakdown: [] }));
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      ...draft,
      sizeBreakdown: informSizes ? draft.sizeBreakdown : [],
      key: editing?.key ?? crypto.randomUUID(),
      unitaryPrice,
      totalPrice,
    });
    setDraft(emptyDraft());
    setInformSizes(false);
  };

  return (
    <div className="overflow-hidden rounded-[14px] border border-border bg-raised">
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-3.5">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-[7px] bg-primary text-[12px] font-bold text-primary-foreground">
          {itemNumber}
        </span>
        <span className="font-display text-[14px] font-bold">
          {editing ? 'Editar camisa' : 'Nova camisa'}
        </span>
        {!canSave && <Badge variant="warning">Incompleta</Badge>}
      </div>

      <div className="space-y-5 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ComponentField label="Tipo de camisa">
            <CatalogSelect
              value={draft.shirtTypeId}
              options={shirtTypes?.data ?? []}
              onChange={handleSelectShirt}
            />
            <PriceInput
              value={draft.shirtTypePrice}
              onChange={(v) => setDraft((d) => ({ ...d, shirtTypePrice: v }))}
            />
          </ComponentField>

          <AnimatePresence mode="popLayout">
            {draft.shirtTypeId && (
              <ComponentField key="sleeve" label="Manga">
                <CatalogSelect
                  value={draft.sleeveTypeId}
                  options={sleeveOptions ?? []}
                  onChange={handleSelectSleeve}
                />
                <PriceInput
                  value={draft.sleeveTypePrice}
                  onChange={(v) => setDraft((d) => ({ ...d, sleeveTypePrice: v }))}
                />
              </ComponentField>
            )}

            {draft.shirtTypeId && (
              <ComponentField key="collar" label="Gola">
                <CatalogSelect
                  value={draft.collarTypeId}
                  options={collarOptions ?? []}
                  onChange={handleSelectCollar}
                />
                <PriceInput
                  value={draft.collarTypePrice}
                  onChange={(v) => setDraft((d) => ({ ...d, collarTypePrice: v }))}
                />
              </ComponentField>
            )}

            {draft.sleeveTypeId && (
              <ComponentField key="fabric" label="Tecido">
                <CatalogSelect
                  value={draft.fabricTypeId}
                  options={fabricOptions ?? []}
                  onChange={handleSelectFabric}
                />
                <PriceInput
                  value={draft.fabricTypePrice}
                  onChange={(v) => setDraft((d) => ({ ...d, fabricTypePrice: v }))}
                />
              </ComponentField>
            )}

            {draft.fabricTypeId && (
              <ComponentField key="print" label="Estampa">
                <CatalogSelect
                  value={draft.printTypeId}
                  options={printOptions ?? []}
                  onChange={handleSelectPrint}
                />
                <PriceInput
                  value={draft.printTypePrice}
                  onChange={(v) => setDraft((d) => ({ ...d, printTypePrice: v }))}
                />
              </ComponentField>
            )}
          </AnimatePresence>
        </div>

        <div className="flex flex-wrap items-end gap-4 border-t border-border pt-5">
          <WizardField label="Quantidade" className="w-[150px]">
            <QuantityStepper
              value={draft.quantity}
              onChange={(quantity) => setDraft((d) => ({ ...d, quantity }))}
              editable
            />
          </WizardField>

          <div className="flex flex-1 flex-wrap items-end gap-2">
            <WizardField label="Opções da peça" className="w-full sm:w-auto sm:flex-1">
              <div className="flex flex-wrap gap-2">
                <ToggleChip
                  checked={informSizes}
                  onCheckedChange={handleToggleInformSizes}
                >
                  Informar tamanho
                </ToggleChip>
                {itemOptions.map((option) => (
                  <ToggleChip
                    key={option.id}
                    checked={draft.selectedOptions.includes(option.id)}
                    onCheckedChange={(checked) => toggleOption(option.id, checked)}
                  >
                    {option.label}
                  </ToggleChip>
                ))}
                <ToggleChip checked={hasArtwork} onCheckedChange={setHasArtwork}>
                  Anexar arte
                </ToggleChip>
              </div>
            </WizardField>
          </div>
        </div>

        {informSizes && (
          <div className="border-t border-border pt-5">
            <SizeBreakdownEditor
              shirtSizes={shirtSizes}
              totalQuantity={draft.quantity}
              breakdown={draft.sizeBreakdown}
              onChange={(sizeBreakdown) => setDraft((d) => ({ ...d, sizeBreakdown }))}
            />
          </div>
        )}

        {hasArtwork && (
          <div className="border-t border-border pt-5">
            <FormUploader
              name="artworkPath"
              control={artworkForm.control}
              label="Arte da estampa"
              sm={12}
              md={6}
              lg={4}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <div className="text-[13px]">
            {missing.length > 0 ? (
              <span className="flex items-center gap-1.5 text-warning">
                <Icon name="circle-alert" size={14} />
                Faltando: <strong className="font-semibold">{missing.join(', ')}</strong>
              </span>
            ) : (
              <span className="text-muted-foreground">
                Unitário:{' '}
                <strong className="font-mono font-semibold text-foreground">
                  R$ {unitaryPrice.toFixed(2)}
                </strong>
                {' · '}
                Total:{' '}
                <strong className="font-mono font-semibold text-foreground">
                  R$ {totalPrice.toFixed(2)}
                </strong>
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {(editing || duplicateFrom) && (
              <Button type="button" variant="outline" onClick={onCancelEdit}>
                Cancelar
              </Button>
            )}
            <Button
              type="button"
              variant="success"
              icon={editing ? 'check' : 'plus'}
              disabled={!canSave}
              onClick={handleSave}
            >
              {editing ? 'Salvar alterações' : 'Adicionar item'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ComponentField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <WizardField label={label}>
        <div className="flex flex-col gap-2">{children}</div>
      </WizardField>
    </motion.div>
  );
}

function PriceInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-[13px] font-mono text-muted-foreground">
        R$
      </span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="h-10 w-full rounded-[10px] border border-border bg-card pl-9 pr-3 text-right font-mono text-[13px] outline-none transition-colors hover:border-border-strong focus-visible:border-primary focus-visible:shadow-[0_0_0_3px_var(--primary-soft)]"
      />
    </div>
  );
}

function SizeBreakdownEditor({
  shirtSizes,
  totalQuantity,
  breakdown,
  onChange,
}: {
  shirtSizes: { id: string; description: string }[];
  totalQuantity: number;
  breakdown: SizeBreakdownEntry[];
  onChange: (breakdown: SizeBreakdownEntry[]) => void;
}) {
  const [selectedSizeId, setSelectedSizeId] = useState('');
  const [rowQuantity, setRowQuantity] = useState(1);

  const assigned = sizeBreakdownTotal(breakdown);
  const remaining = totalQuantity - assigned;
  const canAdd = !!selectedSizeId && rowQuantity > 0 && rowQuantity <= remaining;

  const handleAdd = () => {
    if (!canAdd) return;
    const size = shirtSizes.find((s) => s.id === selectedSizeId);
    if (!size) return;

    onChange([
      ...breakdown,
      { shirtSizeId: selectedSizeId, label: size.description, quantity: rowQuantity },
    ]);
    setSelectedSizeId('');
    setRowQuantity(Math.min(1, Math.max(remaining - rowQuantity, 1)));
  };

  const handleRemove = (index: number) =>
    onChange(breakdown.filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <WizardField label="Tamanho" className="w-40">
          <CatalogSelect
            value={selectedSizeId}
            options={shirtSizes}
            onChange={setSelectedSizeId}
          />
        </WizardField>
        <WizardField label="Qtd." className="w-24">
          <QuantityStepper value={rowQuantity} onChange={setRowQuantity} />
        </WizardField>
        <Button
          type="button"
          variant="outline"
          icon="plus"
          disabled={!canAdd}
          onClick={handleAdd}
        >
          Adicionar tamanho
        </Button>
      </div>

      {breakdown.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {breakdown.map((entry, index) => (
            <Badge
              key={`${entry.shirtSizeId}-${index}`}
              variant="outline"
              className="gap-1.5 pr-1 font-mono"
            >
              {entry.label}: {entry.quantity}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="rounded-full p-0.5 hover:bg-muted"
              >
                <Icon name="x" size={12} />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <p
        className={cn(
          'text-xs',
          remaining === 0
            ? 'text-success'
            : remaining < 0
              ? 'text-destructive'
              : 'text-muted-foreground',
        )}
      >
        {assigned} de {totalQuantity} camisetas com tamanho informado
        {remaining > 0 && ` (faltam ${remaining})`}
        {remaining < 0 && ` (${Math.abs(remaining)} a mais que a quantidade)`}
      </p>
    </div>
  );
}

function CatalogSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { id: string; description: string }[];
  onChange: (id: string) => void;
}) {
  return (
    <WizardSelect
      value={value || undefined}
      onValueChange={onChange}
      options={options.map((option) => ({ label: option.description, value: option.id }))}
    />
  );
}
